import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  LayoutDashboard, Activity, Ticket, Coffee, Grid,
  PlusCircle, List, FileBarChart, ShoppingCart, History,
  Banknote, DollarSign, BookOpen, TrendingUp, Users,
  LogOut, User as UserIcon, Shield, Settings,
  Building2, MapPin, Printer, ChevronRight, X, Menu,
  ChevronDown, PlusSquare, AlertCircle, RefreshCw,
  Clock, CheckCircle, Layers, Timer, ArrowRight,
  Search, Moon, Sun, HeartPulse, Monitor, UserCheck
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const MobileLayout = ({
  activeView,
  onNavigate,
  user,
  logout,
  openCount,
  children,
}) => {
  const { theme, setTheme, themes } = useTheme();
  const [showDrawer, setShowDrawer] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // ─── Tabs definition ────────────────────────────────────
  const tabs = useMemo(() => [
    {
      id: 'home',
      label: 'Home',
      icon: LayoutDashboard,
      view: 'dashboard-page',
      roles: ['admin', 'user', 'viewer'],
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: Activity,
      view: 'list',
      roles: ['admin', 'user', 'viewer'],
    },
    {
      id: 'ticket',
      label: 'Ticket',
      icon: Ticket,
      view: 'tickets',
      roles: ['admin', 'user', 'viewer'],
      badge: openCount > 0 ? openCount : 0,
    },
    {
      id: 'cafe',
      label: 'Cafe',
      icon: Coffee,
      view: 'cafe-pos',
      roles: ['admin', 'user'],
    },
    {
      id: 'more',
      label: 'More',
      icon: Grid,
      view: 'more',
      roles: ['admin', 'user', 'viewer'],
    },
  ], [openCount]);

  // ─── Filter tabs by role ────────────────────────────────
  const visibleTabs = tabs.filter(t => t.roles.includes(user?.role));

  // ─── Determine active tab index ─────────────────────────
  const activeTab = useMemo(() => {
    // Map views to tab ids
    const viewToTab = {
      'dashboard-page': 'home',
      'list': 'activity',
      'add': 'activity',
      'stats': 'activity',
      'tickets': 'ticket',
      'new-ticket': 'ticket',
      'report-tickets': 'ticket',
      'cafe-pos': 'cafe',
      'cafe-dashboard': 'cafe',
      'cafe-sales': 'cafe',
      'cafe-report': 'cafe',
      'print-settings': 'cafe',
    };
    return viewToTab[activeView] || 'more';
  }, [activeView]);

  // ─── More menu items (appear when More tab is active) ───
  const moreMenuSections = useMemo(() => [
    {
      title: 'Antrian',
      items: [
        { id: 'antrian-web', label: 'Antrian Klinik', icon: HeartPulse, url: '/antrian.html', roles: ['admin', 'user', 'viewer'] },
        { id: 'display-tv', label: 'Display TV', icon: Monitor, url: '/display-tv.html', roles: ['admin', 'user', 'viewer'] },
      ],
    },
    {
      title: 'Finance',
      items: [
        { id: 'master-akun', label: 'Master Akun', icon: Banknote, view: 'master-akun', roles: ['admin', 'user'] },
        { id: 'transaksi-add', label: 'Add Transaksi', icon: PlusSquare, view: 'transaksi-add', roles: ['admin', 'user'] },
        { id: 'transaksi', label: 'Transaksi', icon: List, view: 'transaksi', roles: ['admin', 'user'] },
        { id: 'jurnal', label: 'Jurnal Umum', icon: BookOpen, view: 'jurnal', roles: ['admin', 'user'] },
        { id: 'laporan-akhir', label: 'Laporan', icon: TrendingUp, view: 'laporan-akhir', roles: ['admin', 'user'] },
      ],
    },
    {
      title: 'Administration',
      items: [
        { id: 'users', label: 'Master User', icon: Users, view: 'users', roles: ['admin'] },
        { id: 'master-bagian', label: 'Master Bagian', icon: Building2, view: 'master-bagian', roles: ['admin'] },
        { id: 'master-cabang', label: 'Master Cabang', icon: MapPin, view: 'master-cabang', roles: ['admin'] },
        { id: 'hak-akses', label: 'Hak Akses', icon: Settings, view: 'hak-akses', roles: ['admin'] },
      ],
    },
    {
      title: 'Reports',
      items: [
        { id: 'stats', label: 'Report Activity', icon: FileBarChart, view: 'stats', roles: ['admin', 'user'] },
        { id: 'report-tickets', label: 'Report Tickets', icon: Ticket, view: 'report-tickets', roles: ['admin', 'user'] },
        { id: 'cafe-report', label: 'Laporan Cafe', icon: Coffee, view: 'cafe-report', roles: ['admin', 'user'] },
        { id: 'cafe-sales', label: 'Riwayat Penjualan', icon: History, view: 'cafe-sales', roles: ['admin', 'user'] },
        { id: 'print-settings', label: 'Cetak Struk', icon: Printer, view: 'print-settings', roles: ['admin'] },
        { id: 'rekap-absensi', label: 'Rekap Absensi', icon: UserCheck, view: 'rekap-absensi', roles: ['admin', 'user', 'viewer'] },
      ],
    },
  ], []);

  const handleTabPress = (tabId) => {
    if (tabId === 'more') {
      setShowMoreMenu(prev => !prev);
      return;
    }
    setShowMoreMenu(false);
    const tab = tabs.find(t => t.id === tabId);
    if (tab) onNavigate(tab.view);
  };

  const handleMoreItemPress = (item) => {
    setShowMoreMenu(false);
    if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.view) {
      onNavigate(item.view);
    }
  };

  const cycleTheme = () => {
    const currentIdx = themes.findIndex(t => t.id === theme.id);
    const nextIdx = (currentIdx + 1) % themes.length;
    setTheme(themes[nextIdx].id);
  };

  const getRoleBadge = () => {
    const configs = {
      admin: { bg: 'bg-red-100', text: 'text-red-700', label: 'Admin', icon: Shield },
      user: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'User', icon: UserIcon },
      viewer: { bg: 'bg-green-100', text: 'text-green-700', label: 'Viewer', icon: UserIcon },
    };
    return configs[user?.role] || { bg: 'bg-gray-100', text: 'text-gray-700', label: user?.role, icon: UserIcon };
  };

  const roleBadge = getRoleBadge();

  return (
    <div className="mobile-container">
      {/* ─── Mobile Header ───────────────────────────────── */}
      <header className="mobile-header">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-white leading-tight">Activity Tracker</h1>
              <p className="text-[10px] text-white/60 leading-tight">Agus Greneng</p>
            </div>
          </div>

          <div className="flex items-center space-x-1.5">
            {/* Theme toggle */}
            <button
              onClick={cycleTheme}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all"
            >
              {theme.id === 'midnight' ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {/* User avatar */}
            <button
              onClick={() => setShowDrawer(true)}
              className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-xs font-bold text-white"
            >
              {user?.name?.[0] || user?.username?.[0] || 'U'}
            </button>
          </div>
        </div>
      </header>

      {/* ─── Content Area ────────────────────────────────── */}
      <main className="mobile-content px-4 pt-3 pb-2 overflow-y-auto">
        {children}
      </main>

      {/* ─── Bottom Tab Bar ──────────────────────────────── */}
      <nav className="mobile-tab-bar safe-area-bottom">
        <div className="flex items-center justify-around h-full px-2">
          {visibleTabs.map((tab) => {
            const isActive = tab.id === activeTab || (tab.id === 'more' && showMoreMenu);
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabPress(tab.id)}
                className={`mobile-tab-item ${isActive ? 'mobile-tab-active' : ''}`}
              >
                <div className="relative">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  {tab.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full">
                      {tab.badge > 9 ? '9+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium ${isActive ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ─── More Menu Sheet (bottom sheet) ──────────────── */}
      {showMoreMenu && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowMoreMenu(false)} />
          <div className="absolute bottom-16 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up max-h-[65vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 text-sm">Menu</h3>
              <button onClick={() => setShowMoreMenu(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-4 py-3 space-y-4">
              {moreMenuSections.map((section) => {
                const visibleItems = section.items.filter(item =>
                  item.roles.includes(user?.role)
                );
                if (visibleItems.length === 0) return null;

                return (
                  <div key={section.title}>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">
                      {section.title}
                    </p>
                    <div className="space-y-0.5">
                      {visibleItems.map((item) => {
                        const isItemActive = activeView === item.view;
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleMoreItemPress(item)}
                            className={`w-full flex items-center space-x-3 px-3 py-3 rounded-xl text-sm transition-all ${
                              isItemActive
                                ? 'bg-blue-50 text-blue-700 font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${isItemActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <item.icon className={`w-4 h-4 ${isItemActive ? 'text-blue-600' : 'text-gray-500'}`} />
                            </div>
                            <span className="flex-1 text-left">{item.label}</span>
                            <ChevronRight className={`w-4 h-4 ${isItemActive ? 'text-blue-400' : 'text-gray-300'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ─── User Drawer (slide from right) ──────────────── */}
      {showDrawer && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDrawer(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl animate-page-enter-right">
            <div className="p-5">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-gray-900">Account</h3>
                <button onClick={() => setShowDrawer(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* User info */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-lg font-bold text-white">
                  {user?.name?.[0] || user?.username?.[0] || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{user?.name || user?.username}</p>
                  <div className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${roleBadge.bg} ${roleBadge.text} mt-1`}>
                    <roleBadge.icon className="w-3 h-3" />
                    <span>{roleBadge.label}</span>
                  </div>
                </div>
              </div>

              {user?.bagian && (
                <div className="text-xs text-gray-500 px-1 mb-4">
                  <span className="font-medium">Bagian:</span> {user.bagian}
                </div>
              )}

              {/* Theme selection */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Theme</p>
                <div className="flex flex-wrap gap-2">
                  {themes.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setTheme(t.id)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        theme.id === t.id ? 'border-gray-900 scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: t.swatch || '#2563eb' }}
                      title={t.name}
                    />
                  ))}
                </div>
              </div>

              {/* Logout */}
              <button
                onClick={logout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 text-red-600 rounded-xl font-semibold text-sm hover:bg-red-100 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileLayout;
